import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useClientLicenses } from "@/hooks/useClientLicenses";
import { useClients } from "@/hooks/useClients";
import { useModulePrices } from "@/hooks/useModulePrices";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface NewSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewSubscriptionDialog = ({ open, onOpenChange }: NewSubscriptionDialogProps) => {
  const { addClientLicense, isAddingLicense } = useClientLicenses();
  const { clients } = useClients();
  const { modulePrices } = useModulePrices();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [clientId, setClientId] = useState('');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [contractedSeats, setContractedSeats] = useState(1);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [status, setStatus] = useState<'active' | 'trial'>('active');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [trialEndDate, setTrialEndDate] = useState<Date>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeModules = modulePrices.filter(m => m.is_active);
  const availableClients = clients.filter(c => c.status === 'active');

  const toggleModule = (code: string) => {
    setSelectedModules(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const monthlyCost = selectedModules.reduce((sum, code) => {
    const mod = modulePrices.find(m => m.code === code);
    return sum + (mod?.price || 0);
  }, 0) * contractedSeats;

  const annualCost = monthlyCost * 10; // Pague 10 leve 12

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || selectedModules.length === 0) return;

    setIsSubmitting(true);
    try {
      // 1. Create the client_license (use first license_plan as fallback - required by FK)
      const { data: plans } = await supabase.from('license_plans').select('id').limit(1);
      const licensePlanId = plans?.[0]?.id;
      if (!licensePlanId) {
        toast({ title: "Erro", description: "Nenhum plano base encontrado no sistema.", variant: "destructive" });
        return;
      }

      const { data: license, error: licenseError } = await supabase
        .from('client_licenses')
        .insert({
          client_id: clientId,
          license_plan_id: licensePlanId,
          status,
          billing_cycle: billingCycle,
          start_date: format(startDate, 'yyyy-MM-dd'),
          contracted_seats: contractedSeats,
          trial_end_date: trialEndDate ? format(trialEndDate, 'yyyy-MM-dd') : null,
        })
        .select()
        .single();

      if (licenseError) throw licenseError;

      // 2. Create client_modules for selected modules
      const moduleInserts = selectedModules.map(code => ({
        client_id: clientId,
        module_code: code,
        is_active: true,
        activated_at: new Date().toISOString(),
        source: 'admin',
      }));

      const { error: modulesError } = await supabase
        .from('client_modules')
        .insert(moduleInserts);

      if (modulesError) throw modulesError;

      queryClient.invalidateQueries({ queryKey: ['client-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['client-modules'] });
      toast({ title: "Assinatura criada", description: `Assinatura criada para o cliente com ${selectedModules.length} módulo(s).` });
      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      toast({ title: "Erro ao criar assinatura", description: error.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setClientId('');
    setSelectedModules([]);
    setContractedSeats(1);
    setBillingCycle('monthly');
    setStatus('active');
    setStartDate(new Date());
    setTrialEndDate(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Assinatura</DialogTitle>
          <DialogDescription>Configure uma nova assinatura modular para o cliente</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cliente */}
          <div>
            <Label>Cliente *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
              <SelectContent>
                {availableClients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Módulos */}
          <div>
            <Label className="mb-2 block">Módulos *</Label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
              {activeModules.map(mod => (
                <div key={mod.code} className="flex items-center space-x-2">
                  <Checkbox
                    id={`mod-${mod.code}`}
                    checked={selectedModules.includes(mod.code)}
                    onCheckedChange={() => toggleModule(mod.code)}
                  />
                  <label htmlFor={`mod-${mod.code}`} className="text-sm cursor-pointer flex-1">
                    {mod.name}
                    <span className="text-muted-foreground ml-1">
                      (R$ {mod.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Seats e Ciclo */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="seats">Licenças (Seats) *</Label>
              <Input
                id="seats"
                type="number"
                min="1"
                value={contractedSeats}
                onChange={(e) => setContractedSeats(parseInt(e.target.value) || 1)}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={(v: 'active' | 'trial') => setStatus(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativa</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ciclo de Cobrança</Label>
              <Select value={billingCycle} onValueChange={(v: 'monthly' | 'yearly') => setBillingCycle(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensal</SelectItem>
                  <SelectItem value="yearly">Anual (10x)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Data de Início */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data de Início *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP", { locale: ptBR }) : "Selecione"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus className="pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
            {status === 'trial' && (
              <div>
                <Label>Fim do Trial</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !trialEndDate && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {trialEndDate ? format(trialEndDate, "PPP", { locale: ptBR }) : "Selecione"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={trialEndDate} onSelect={setTrialEndDate} initialFocus className="pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>

          {/* Resumo de custo */}
          {selectedModules.length > 0 && (
            <div className="bg-muted rounded-lg p-4 space-y-1">
              <h4 className="font-medium text-sm">Resumo do custo</h4>
              <div className="text-sm">
                {selectedModules.length} módulo(s) × {contractedSeats} licença(s)
              </div>
              <div className="text-lg font-bold">
                {billingCycle === 'monthly'
                  ? `R$ ${monthlyCost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês`
                  : `R$ ${annualCost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/ano (pague 10, leve 12)`
                }
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isSubmitting || !clientId || selectedModules.length === 0}>
              {isSubmitting ? 'Criando...' : 'Criar Assinatura'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
